import { shouldDrawOverlay, isInPlaceCover } from './sourceEditPolicy.js';
import { visualPointToPdf } from './overlayGeometry.js';
import { normalizeAngle } from './geometry.js';
import { degrees, rgb } from 'pdf-lib';
import { wrapTextLines } from './textLayout.js';
import { fontShorthand } from './fontCatalog.js';

/** Convert editable browser text into a transparent PNG, preserving Turkish
 * glyphs and the visual line wrapping. It remains editable in PDF Studio until
 * export. In the exported PDF text is flattened as an image (not selectable). */
export async function rasterizeText(item) {
  if (typeof document === 'undefined') throw new Error('Metin önizlemesi için tarayıcı gerekir.');
  const fontSize = Math.max(6, Math.min(144, Number(item.fontSize) || 18));
  const width = Math.max(1, item.width);
  const height = Math.max(1, item.height);
  const scale = 3;
  const selectedFont = fontShorthand(fontSize, item.fontFamily, item);
  try {
    // Wait for the bundled font (including Turkish glyphs) before measuring
    // the line breaks and painting the exported PDF layer.
    await document.fonts?.load(selectedFont, String(item.text || 'Türkçe İıŞşĞğÇçÖöÜü'));
  } catch { /* Browser fallback still generates the PDF if font loading fails. */ }
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(width * scale));
  canvas.height = Math.max(1, Math.ceil(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Metin çizim alanı oluşturulamadı.');
  ctx.scale(scale, scale);
  ctx.font = selectedFont;
  ctx.textBaseline = 'top';
  // Letter-spacing is painted explicitly for consistent output across browsers.
  ctx.fillStyle = /^#[\da-f]{6}$/i.test(item.color || '') ? item.color : '#1e293b';
  const lineHeight = fontSize * (Number(item.lineSpacing) || 1.25);
  const spacing = Number(item.letterSpacing) || 0;
  const measure = (value) => spacing ? [...value].reduce((sum,c)=>sum+ctx.measureText(c).width,0)
    + Math.max(0,[...value].length-1)*spacing : ctx.measureText(value).width;
  const paintText = (text,x,y) => {
    if (!spacing) {ctx.fillText(text,x,y);return;}
    let px=x;
    for(const char of text){ctx.fillText(char,px,y);px+=ctx.measureText(char).width+spacing;}
  };
  const lines = wrapTextLines(item.text, width, measure);
  if (item.backgroundColor && /^#[\da-f]{6}$/i.test(item.backgroundColor)) {
    ctx.fillStyle = item.backgroundColor; ctx.fillRect(0, 0, width, height);
  }
  ctx.fillStyle = /^#[\da-f]{6}$/i.test(item.color || '') ? item.color : '#1e293b';
  lines.forEach((line, i) => {
    const y = i * lineHeight + fontSize * 0.1;
    if (y + fontSize > height + 1) return;
    const textWidth = measure(line);
    const x = item.textAlign === 'right' ? Math.max(0,width-textWidth)
      : item.textAlign === 'center' ? Math.max(0,(width-textWidth)/2) : 0;
    paintText(line, x, y);
    if (item.underline || item.strikeThrough) {
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = Math.max(0.6,fontSize/16);
      for (const lineY of [item.underline ? y+fontSize*1.03 : null, item.strikeThrough ? y+fontSize*0.55 : null]) {
        if (lineY == null) continue;
        ctx.beginPath(); ctx.moveTo(x,lineY); ctx.lineTo(x+textWidth,lineY); ctx.stroke();
      }
    }
  });
  return canvas.toDataURL('image/png');
}

/** Overlays are independent editable objects until this final PDF save. */
export async function drawPageOverlays(pdf, page, overlays) {
  if (!overlays?.length) return;
  const crop = page.getCropBox();
  const rotation = normalizeAngle(page.getRotation().angle);
  const imageCache = new Map();
  for (const item of overlays) {
    if (!shouldDrawOverlay(item) || !Number.isFinite(item.width) || !Number.isFinite(item.height)) continue;
    if (item.width <= 0 || item.height <= 0) continue;
    const { x, y, width, height } = item;
    let embedded;
    if (item.type === 'image') {
      if (!item.bytes?.byteLength) continue;
      const key = item.previewUrl || item.id;
      if (!imageCache.has(key)) {
        imageCache.set(key, item.mime === 'image/png'
          ? await pdf.embedPng(item.bytes) : await pdf.embedJpg(item.bytes));
      }
      embedded = imageCache.get(key);
    } else if (item.type === 'text') {
      if (!String(item.text || '').length) continue;
      embedded = (item.originalSnapshotBytes?.length && !item.editDirty)
        ? await pdf.embedPng(item.originalSnapshotBytes)
        : await pdf.embedPng(await rasterizeText(item));
    } else continue;
    const origin = visualPointToPdf(x, y + height, crop, rotation);
    if (isInPlaceCover(item)) {
      // The original PDF artwork is left intact. An opaque background covers
      // the selected box and the user-selected replacement is layered on top.
      // This is not PDF redaction: original operators remain in the file.
      const hex=/^#[0-9a-f]{6}$/i.test(item.coverColor || '') ? item.coverColor : '#ffffff';
      const components=[1,3,5].map(i => parseInt(hex.slice(i,i+2),16)/255);
      page.drawRectangle({ x: origin.x,y:origin.y,width,height,
        rotate:degrees(rotation),color:rgb(...components) });
    }
    page.drawImage(embedded, {
      x: origin.x, y: origin.y, width, height,
      rotate: degrees(rotation),
      opacity: item.type === 'image' ? (item.opacity ?? 1) : 1,
    });
  }
}
